import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useProject } from '../hooks/useProject'
import { useTimePeriods } from '../hooks/useCurvaS'
import { useTasks } from '../hooks/useTasks'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs'
import { GanttChart } from '../components/charts/GanttChart'
import { CalendarChart } from '../components/charts/CalendarChart'

const SchedulePage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const numericProjectId = projectId ? parseInt(projectId) : 0
  
  const [activeTab, setActiveTab] = useState('gantt')
  
  const { data: project, isLoading: isLoadingProject } = useProject(numericProjectId)
  const { data: tasksData, isLoading: isLoadingTasks } = useTasks(numericProjectId)
  const { data: periodsData, isLoading: isLoadingPeriods } = useTimePeriods(numericProjectId, 'week')
  
  useEffect(() => {
    if (!projectId) {
      navigate('/projects')
    }
  }, [projectId, navigate])
  
  if (isLoadingProject || isLoadingTasks || isLoadingPeriods) {
    return <LoadingSkeleton />
  }
  
  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Project not found</p>
        </div>
      </div>
    )
  }
  
  const criticalPathIds = tasksData?.criticalPath || []
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-gray-600 mt-1">Project Schedule & Timeline</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-gray-600">Total Tasks</p>
            <p className="font-semibold text-gray-900">{tasksData?.totalTasks || 0}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-gray-600">Total Periods</p>
            <p className="font-semibold text-gray-900">{periodsData?.periods.length || 0}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-gray-600">Critical Path</p>
            <p className="font-semibold text-gray-900">{criticalPathIds.length}</p>
          </div>
          <div className="bg-gray-50 p-3 rounded">
            <p className="text-gray-600">Project Duration</p>
            <p className="font-semibold text-gray-900">{tasksData?.tasks && tasksData.tasks.length > 0 ? Math.ceil((new Date(tasksData.tasks[0].endDate).getTime() - new Date(tasksData.tasks[0].startDate).getTime()) / (1000 * 60 * 60 * 24)) : 0} days</p>
          </div>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="gantt">Gantt Chart</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
        </TabsList>
        
        <TabsContent value="gantt">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <GanttChart
              tasks={tasksData?.tasks || []}
              criticalPath={criticalPathIds}
              height={600}
            />
          </div>
        </TabsContent>
        
        <TabsContent value="calendar">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm mt-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Calendar View</h2>
            <CalendarChart
              tasks={tasksData?.tasks || []}
              criticalPath={criticalPathIds}
              height={400}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

SchedulePage.displayName = 'SchedulePage'
export default SchedulePage